/**
 * Minimal SFX player over the vendored Kenney .ogg files. No external audio dep.
 * Each logical key gets a small pool of HTMLAudioElements for overlap. Browsers
 * gate autoplay until a user gesture; the first keypress unlocks it, and any
 * call before then is silently ignored (audio is not load-bearing for Step 1).
 *
 * POLISH_07 #5: a master SFX gain multiplies into every per-call volume so the pause-menu
 * SFX slider can turn gameplay sound down/off as a whole, independent of music (previously
 * only a dev-only mute existed — DebugOverlay.ts). Persisted to localStorage like Music's
 * volume/mute (Sound lives under src/engine but is, like Music, background/feedback flavour
 * OUTSIDE the deterministic sim — nothing in World reads audio state back into the sim).
 */
import { Rng } from './Rng';
import { parseStoredVolume } from './Music';

const SFX_VOL_KEY = 'maker.sfxVol.v1';

function readStoredSfxVolume(fallback: number): number {
  try { return parseStoredVolume(localStorage.getItem(SFX_VOL_KEY), fallback); } catch { return fallback; }
}
function writeStoredSfxVolume(v: number): void {
  try { localStorage.setItem(SFX_VOL_KEY, String(v)); } catch { /* private mode */ }
}

export class Sound {
  private pools = new Map<string, HTMLAudioElement[]>();
  private idx = new Map<string, number>();
  private muted = false;
  /** Master SFX gain (0..1), multiplied into every play() call's volume. Default 1 = no
   *  attenuation (existing per-call volumes are unchanged unless the player turns it down). */
  private gain = readStoredSfxVolume(1);
  private readonly poolSize = 4;
  /** Own, ISOLATED PRNG for the ±jitter on every clip (kills machine-gun repetition).
   *  NOT world.rng — this stream must never perturb the sim's RNG state, or a muted run
   *  and an unmuted run could diverge. Audio is fire-and-forget: nothing reads it back,
   *  so a private seeded stream is deterministic-but-inert w.r.t. replay parity. */
  private rng = new Rng(0x5f2ac10b);

  /** Build pools from a logical-key → URL map (manifest.sounds). */
  load(sounds: Record<string, string>): void {
    for (const [key, url] of Object.entries(sounds)) {
      const pool: HTMLAudioElement[] = [];
      for (let i = 0; i < this.poolSize; i++) {
        const a = new Audio(url);
        a.preload = 'auto';
        a.volume = 0.5;
        // playbackRate shifts PITCH only when preservesPitch is off — that is the whole
        // point of the jitter, so disable pitch preservation once at build time.
        a.preservesPitch = false;
        pool.push(a);
      }
      this.pools.set(key, pool);
      this.idx.set(key, 0);
    }
  }

  setMuted(m: boolean): void { this.muted = m; }

  /** Master SFX gain (0..1) — multiplies into every play() call's volume. Persisted. */
  setGain(g: number): void { this.gain = Math.min(1, Math.max(0, g)); writeStoredSfxVolume(this.gain); }
  getGain(): number { return this.gain; }

  /**
   * Play a clip. `pitch` is a base playback-rate multiplier (1 = normal; the coin-chain
   * passes a rising value); a small random ±6% jitter is layered on top so repeated sounds
   * never machine-gun. `volume` 0..1 (per-call base, before the master gain multiplies in).
   */
  play(key: string, volume = 0.5, pitch = 1): void {
    if (this.muted || this.gain <= 0) return;
    const pool = this.pools.get(key);
    if (!pool) return;
    const i = this.idx.get(key)!;
    const a = pool[i];
    this.idx.set(key, (i + 1) % pool.length);
    a.volume = volume * this.gain;
    a.playbackRate = pitch * this.rng.range(0.94, 1.06);
    a.currentTime = 0;
    void a.play().catch(() => { /* autoplay gate before first gesture — ignore */ });
  }

  /** Stop + release every pooled audio element (called from World.destroy when a
   *  playtest ends, so retired levels leave no playing audio or dangling elements). */
  destroy(): void {
    for (const pool of this.pools.values()) {
      for (const a of pool) { a.pause(); a.removeAttribute('src'); a.load(); }
    }
    this.pools.clear();
    this.idx.clear();
  }
}
